import { useEffect, useState } from 'react';
import { Form, FormField, TextField } from '@melio/penny';

import { lookupIndustries, lookupAddresses } from '@/api/lookup';
import type { AddressSuggestion } from '@/api/lookup';
import type { EnvironmentId } from '@/config/environments';

// penny-fields.jsx — the two reference-lookup form inputs the onboarding KYC/KYB form needs,
// rebuilt on @melio/penny primitives and wired to the unauthenticated `/v2/lookup/*` helpers
// (see src/api/lookup.ts). These mirror platform-app: the industry field is the equivalent of
// @melio/form-controls' IndustryTypeSelectWidget, and the address field is the equivalent of
// @melio/platform-address' AddressFormInput. Those app-level packages can't be imported into this
// self-contained portal, so we reproduce the same UX (penny Form.Search typeahead + manual fields)
// against the public v2 lookup endpoints instead.

// The `form` prop comes from @melio/penny's useForm — typed loosely to avoid coupling to penny's
// generics.
type PennyForm = any;

interface IndustryValue {
  name: string;
  naicsCode: string | null;
}

interface IndustryOption {
  label: string;
  value: IndustryValue;
  description?: string;
}

interface IndustryFieldProps {
  form: PennyForm;
  env: EnvironmentId;
  name?: string;
  label?: string;
  isRequired?: boolean;
  colSpan?: number;
  /** Partner key to authenticate the lookup call, when the caller already holds one. */
  apiKey?: string;
  /** Signed-in user's access token supplier — sends `Authorization: Bearer` on the lookup call. */
  getToken?: () => Promise<string | null>;
}

/**
 * NAICS industry typeahead. Stores `{ name, naicsCode }` on the form `name` field — the same shape
 * platform-app's IndustryTypeSelectWidget stores. Free-typed values become a creatable option with
 * a null naicsCode (the form's validation requires a real, resolved code before continuing).
 */
export function IndustryField({
  form,
  env,
  name = 'industry',
  label = 'Industry',
  isRequired,
  colSpan = 2,
  apiKey,
  getToken,
}: IndustryFieldProps) {
  const { registerField, setValue } = form;
  const [options, setOptions] = useState<IndustryOption[]>([]);
  const [term, setTerm] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!term || term.length < 2) {
      setOptions([]);
      return undefined;
    }
    const ctrl = new AbortController();
    setLoading(true);
    lookupIndustries(env, term, { signal: ctrl.signal, apiKey, getToken })
      .then((res) =>
        setOptions(
          res.map((o) => ({
            label: o.title,
            value: { name: o.title, naicsCode: o.naicsCode == null ? null : String(o.naicsCode) },
            description: o.subtitle,
          }))
        )
      )
      .catch(() => {})
      .finally(() => setLoading(false));
    return () => ctrl.abort();
  }, [term, env, apiKey, getToken]);

  const handleCreate = (input: string) => {
    const option: IndustryOption = { label: input, value: { name: input, naicsCode: null } };
    setOptions((prev) => [...prev, option]);
    setValue(name, option.value, { shouldValidate: true, shouldDirty: true });
  };

  return (
    <Form.Search
      {...registerField(name)}
      colSpan={colSpan}
      isRequired={isRequired}
      labelProps={{ label }}
      debounce={300}
      options={options}
      isLoading={loading}
      onInputChange={setTerm}
      emptyState={{ label: term.length < 2 ? 'Type to search industries' : 'No matching industries' }}
      formatSelectedValue={(option: IndustryOption) => (option.value && option.value.name) || option.label}
      creatableOption={{ label: (value: string) => `Use “${value}”`, onClick: handleCreate }}
      filterOptions={(opts: IndustryOption[]) => opts}
      placeholder="Search your industry (NAICS)"
    />
  );
}

interface AddressOption {
  label: string;
  value: AddressSuggestion;
}

interface AddressFieldsProps {
  form: PennyForm;
  env: EnvironmentId;
  parentFieldName: string;
  label?: string;
  /** Partner key to authenticate the lookup call, when the caller already holds one. */
  apiKey?: string;
  /** Signed-in user's access token supplier — sends `Authorization: Bearer` on the lookup call. */
  getToken?: () => Promise<string | null>;
}

/**
 * US address block: a penny Form.Search autocomplete (domestic suggestions from `/v2/lookup/addresses`
 * carry their parsed parts inline) that fills the editable line1/line2/city/state/postal fields below
 * it — the same "autocomplete fills the manual fields" model as platform-app's AddressFormInput. The
 * search itself is held on a throwaway `${parentFieldName}._search` field that is never submitted.
 */
export function AddressFields({ form, env, parentFieldName, label = 'Address', apiKey, getToken }: AddressFieldsProps) {
  const { registerField, setValue, watch } = form;
  const [options, setOptions] = useState<AddressOption[]>([]);
  const [term, setTerm] = useState('');
  const [loading, setLoading] = useState(false);

  const searchName = `${parentFieldName}._search`;
  const selected: AddressSuggestion | undefined = watch(searchName);

  useEffect(() => {
    if (!term || term.length < 3) {
      setOptions([]);
      return undefined;
    }
    const ctrl = new AbortController();
    setLoading(true);
    lookupAddresses(env, term, { type: 'domestic', signal: ctrl.signal, apiKey, getToken })
      .then((res) => setOptions(res.map((s) => ({ label: s.formattedText, value: s }))))
      .catch(() => {})
      .finally(() => setLoading(false));
    return () => ctrl.abort();
  }, [term, env, apiKey, getToken]);

  // When a suggestion is picked, spread its parsed parts into the editable fields below.
  // penny's Form.Search may hand back either the option's `value` (the suggestion) or the wrapping
  // option object depending on the selection path, so read `addressParts` from both levels.
  useEffect(() => {
    const sel = selected as (AddressSuggestion & { value?: AddressSuggestion }) | undefined;
    const parts = sel?.addressParts ?? sel?.value?.addressParts;
    if (!parts) return;
    setValue(`${parentFieldName}.line1`, parts.line1 || '', { shouldValidate: true, shouldDirty: true });
    setValue(`${parentFieldName}.line2`, parts.line2 || '', { shouldDirty: true });
    setValue(`${parentFieldName}.city`, parts.city || '', { shouldValidate: true, shouldDirty: true });
    setValue(`${parentFieldName}.state`, parts.state || '', { shouldValidate: true, shouldDirty: true });
    setValue(`${parentFieldName}.postalCode`, parts.postalCode || '', { shouldValidate: true, shouldDirty: true });
  }, [selected]);

  return (
    <>
      <Form.Search
        {...registerField(searchName)}
        colSpan={2}
        labelProps={{ label }}
        debounce={300}
        options={options}
        isLoading={loading}
        onInputChange={setTerm}
        emptyState={{ label: term.length < 3 ? 'Type to search your address' : 'No matching addresses' }}
        formatSelectedValue={(option: AddressOption) => (option.value && option.value.formattedText) || option.label}
        filterOptions={(opts: AddressOption[]) => opts}
        placeholder="Search for your address"
      />
      <FormField
        colSpan={2}
        isRequired
        {...registerField(`${parentFieldName}.line1`)}
        labelProps={{ label: 'Address line 1 *' }}
        render={(props: any) => <TextField {...props} autoComplete="address-line1" placeholder="123 Main St" />}
      />
      <FormField
        colSpan={2}
        {...registerField(`${parentFieldName}.line2`)}
        labelProps={{ label: 'Address line 2' }}
        render={(props: any) => <TextField {...props} placeholder="Suite, floor, unit (optional)" />}
      />
      <FormField
        isRequired
        {...registerField(`${parentFieldName}.city`)}
        labelProps={{ label: 'City *' }}
        render={(props: any) => <TextField {...props} placeholder="San Francisco" />}
      />
      <FormField
        isRequired
        {...registerField(`${parentFieldName}.state`)}
        labelProps={{ label: 'State *' }}
        render={(props: any) => <TextField {...props} placeholder="CA" />}
      />
      <FormField
        isRequired
        {...registerField(`${parentFieldName}.postalCode`)}
        labelProps={{ label: 'Postal code *' }}
        render={(props: any) => <TextField {...props} placeholder="94105" />}
      />
      <FormField
        {...registerField(`${parentFieldName}.aptNumber`)}
        labelProps={{ label: 'Apt / unit number' }}
        render={(props: any) => <TextField {...props} placeholder="(optional)" />}
      />
    </>
  );
}
